import { ReactElement, useEffect, useRef } from 'react';
import { useLocation, useNavigationType, NavigationType } from 'react-router';

const scrollPositions = new Map<string, number>();

const ScrollPositionRestoration = (): ReactElement | null => {
  const { key: locationKey } = useLocation();
  const navigationType = useNavigationType();
  const locationKeyRef = useRef(locationKey);
  const hasMountedRef = useRef(false);

  useEffect(() => {
    locationKeyRef.current = locationKey;
  }, [locationKey]);

  useEffect(() => {
    const saveScrollPosition = (): void => {
      scrollPositions.set(locationKeyRef.current, window.scrollY);
    };

    saveScrollPosition();
    window.addEventListener('scroll', saveScrollPosition, { passive: true });

    return () => window.removeEventListener('scroll', saveScrollPosition);
  }, []);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    if (navigationType !== NavigationType.Pop) {
      return;
    }

    const savedScrollPosition = scrollPositions.get(locationKey);
    if (savedScrollPosition === undefined) {
      return;
    }

    const restoreScrollPosition = (): void => {
      window.scrollTo({ top: savedScrollPosition, behavior: 'auto' });
    };

    const animationFrameId = requestAnimationFrame(restoreScrollPosition);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [locationKey, navigationType]);

  return null;
};

export default ScrollPositionRestoration;
